import { useId, useState, type FormEvent } from "react";
import { CheckCircle2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { submitWhatsAppConsent, type PublicInvoiceResponse } from "@/api/public";

// Secondary to the Pay button: a plain card with an outline button, never brand-filled.
// Only while the invoice is still being chased: a paid, voided or credited one has no reminders left to send.
const CHASED = new Set(["Issued", "PartiallyPaid"]);

export function WhatsAppOptIn({
  token, invoice, mock = false,
}: { token: string; invoice: PublicInvoiceResponse; mock?: boolean }) {
  const [optedIn, setOptedIn] = useState(invoice.whatsAppOptedIn);
  const [hidden, setHidden] = useState(false);
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const phoneId = useId();
  const consentId = useId();
  const errorId = useId();

  if (optedIn) {
    return (
      <p role="status" className="mt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground">
        <CheckCircle2 aria-hidden className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        You'll get updates on WhatsApp
      </p>
    );
  }
  if (hidden || !invoice.whatsAppOptInAvailable || !CHASED.has(invoice.status)) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting || !consent || !phone.trim()) return;
    setSubmitting(true);
    setPhoneError(null);
    setFormError(null);
    // Mock mode (a ?mock= preview) never calls the backend.
    const result = mock ? ({ ok: true } as const) : await submitWhatsAppConsent(token, phone.trim());
    setSubmitting(false);
    if (result.ok) {
      setOptedIn(true);
      return;
    }
    switch (result.error.kind) {
      case "invalid-phone":
        setPhoneError(result.error.message);
        return;
      case "unavailable":
        setHidden(true);
        return;
      case "network":
        setFormError("Couldn't reach the server. Check your connection and try again.");
        return;
      default:
        console.error("WhatsApp consent error", { token, error: result.error });
        setFormError("We couldn't save that. Please try again.");
    }
  }

  return (
    <Card className="mt-4 gap-4 shadow-none">
      <CardHeader>
        <CardTitle className="text-base">Get updates on WhatsApp</CardTitle>
        <CardDescription>
          Receive future invoices and payment reminders from {invoice.organisationName} on WhatsApp.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor={phoneId}>WhatsApp number</Label>
            <Input
              id={phoneId}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="+44 7700 900123"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                setPhoneError(null);
              }}
              aria-invalid={phoneError ? true : undefined}
              aria-describedby={errorId}
            />
            <p id={errorId} aria-live="polite" className={phoneError ? "text-sm text-destructive" : "text-xs text-muted-foreground"}>
              {phoneError ?? "Include your country code, for example +44 7700 900123."}
            </p>
          </div>
          <div className="flex items-start gap-3">
            <Checkbox
              id={consentId}
              checked={consent}
              onCheckedChange={(v) => setConsent(v === true)}
              className="mt-0.5"
            />
            <Label htmlFor={consentId} className="block text-sm leading-snug font-normal text-muted-foreground">
              I agree to receive invoices and reminders from {invoice.organisationName} on WhatsApp. Reply STOP at
              any time to opt out.
            </Label>
          </div>
          {formError ? (
            <p role="alert" className="text-sm text-destructive">
              {formError}
            </p>
          ) : null}
          <Button type="submit" variant="outline" className="w-full" disabled={!consent || !phone.trim() || submitting}>
            {submitting ? <Spinner className="h-4 w-4" /> : "Get WhatsApp updates"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
